import React, {ReactNode} from 'react';
import styled, {css} from 'styled-components';
import {Tooltip} from '@forecast-it/design-system';
import {useIsTruncated} from '../useIsTruncated';

/** Clamps a cell's content to `lines` lines (one by default), with a tooltip carrying the full value
 * shown only once the content is actually cut off.
 *
 * The clamp lives here rather than on the design-system component because `Link` implements its own
 * `ellipsis` with `white-space: nowrap`, whose minimum width is the entire unbroken string. The
 * table is sized `width: max-content`, so a cell that cannot shrink drags its whole column out to
 * the longest value in the data and nothing is ever clipped. The `word-break: break-all` clamp used
 * here — the same one `Text` and `SubHeading` apply for their `ellipsis` — lets the cell shrink to
 * whatever width the column gives it. Pass children WITHOUT their own `ellipsis` prop.
 *
 * `Link` and `SubHeading` also have no equivalent of `Text`'s `tooltipOnEllipsis`, so the
 * truncation-gated tooltip is assembled here too, rather than being a prop on the child.
 *
 * More than one line is for prose, such as the project-status note. It breaks on words and keeps the
 * author's own line breaks instead, because `break-all` across several lines cuts the text mid-word.
 * `overflow-wrap: anywhere` still lets one long unbroken token shrink to the column. */
const ClampedLine = styled.span<{$lines: number}>`
	display: -webkit-box;
	-webkit-line-clamp: ${({$lines}) => $lines};
	-webkit-box-orient: vertical;
	overflow: hidden;
	${({$lines}) =>
		$lines > 1
			? css`
					white-space: pre-wrap;
					overflow-wrap: anywhere;
			  `
			: css`
					word-break: break-all;
			  `}

	> * {
		display: inline;
	}
`;

/** The element the tooltip anchors to, and the box the clamp cuts.
 *
 * A plain span rather than the caller's component, because the design system's `Tooltip` puts
 * Radix's `asChild` on its trigger, which composes a ref into whatever child it is handed. `Link`
 * forwards refs and copes; `SubHeading` is a plain function component, so handing it straight to
 * the trigger makes React warn that function components cannot be given refs, and leaves the popper
 * with no anchor — the tooltip renders in the wrong place or not at all. A span always takes the
 * ref, so the trigger no longer depends on which component the caller passed.
 *
 * `display: inline` on it and on its child is what lets the clamp above do its work: design-system
 * text components render as inline-block, a single atomic box the clamp cannot break, so the text
 * would otherwise overflow sideways with no ellipsis and never grow past one line for the
 * truncation check to fire either. */
const TooltipAnchor = styled.span`
	display: inline;

	> * {
		display: inline;
	}
`;

/** `ClampedLine` — the element being measured — stays mounted at a stable position regardless of
 * `isTruncated`, so detecting truncation never unmounts the node under measurement. The anchor
 * inside it is present in both branches too, so the flip adds the tooltip's behaviour without
 * changing the shape of what is measured.
 *
 * `interactable` is set deliberately, and not for hover behaviour. The design system's
 * non-interactable trigger calls `preventDefault()` on click and wraps the children in a
 * `width: fit-content` div carrying `tabIndex={0}`. All three are wrong here: cancelling the click
 * kills cmd/ctrl-click on a cell whose whole point is being a real link, the extra div is
 * block-level and stops the clamp above it from ever drawing an ellipsis, and the tab stop lands on
 * a wrapper rather than the link itself, once per truncated row. The interactable path renders the
 * children directly and does none of it. */
export function TruncatedCellContent({
	tooltip,
	lines = 1,
	children,
}: {
	tooltip: string;
	lines?: number;
	children: ReactNode;
}): JSX.Element {
	const [ref, isTruncated] = useIsTruncated();

	return (
		// `data-cy` marks the element being measured. Tests have to reach it to simulate overflow,
		// and the wrappers between it and the text differ by branch.
		<ClampedLine ref={ref} $lines={lines} data-cy="truncated-cell-content">
			{isTruncated ? (
				<Tooltip content={tooltip} interactable>
					<TooltipAnchor>{children}</TooltipAnchor>
				</Tooltip>
			) : (
				<TooltipAnchor>{children}</TooltipAnchor>
			)}
		</ClampedLine>
	);
}
